import React, { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeft, Home } from 'lucide-react';
import { LyraAvatar } from '@/components/LyraAvatar';
import { useAuth } from '@/contexts/AuthContext';
import { usePersonalizationData } from '@/hooks/usePersonalizationData';
import { useNavigate } from 'react-router-dom';
import { getUserRoleIconUrl, getRoleMessaging, getDynamicRoleMessaging } from '@/utils/supabaseIcons';

interface PersonalizationFlowProps {
  onComplete: () => void;
}

// Storyline helper functions for current selection context
const getRoleStoryline = (role: string): string => {
  const storylines = {
    fundraising: "You're the person who brings in the resources that make everything possible. AI will help you write grants that actually get funded and create donor messages that build real relationships instead of sounding corporate.",
    programs: "You see the impact happening every day with participants. AI will help you capture those success stories and turn your program data into compelling narratives that show funders exactly why your work matters.",
    operations: "You keep everything running smoothly behind the scenes. AI will help you automate the repetitive stuff so you can focus on the strategic work that actually moves your organization forward.",
    marketing: "You're the voice that connects your mission with the community. AI will help you create content that actually engages people and tells your story in ways that inspire action, not just awareness.",
    it: "You're building the foundation that enables everyone else's work. AI will help you implement solutions that staff actually want to use, making technology feel helpful instead of frustrating.",
    leadership: "You're guiding the organization's direction and culture. AI will help you communicate your vision clearly and make decisions based on real insights, not just gut feeling."
  };
  return storylines[role as keyof typeof storylines] || "AI will help you work more effectively in your unique role, giving you tools that actually enhance what you do best.";
};

const getTechComfortStoryline = (techComfort: string): string => {
  const storylines = {
    beginner: "We'll start with simple, intuitive tools that feel natural from day one. You'll build confidence gradually as you see immediate results in your daily work.",
    intermediate: "You're in the sweet spot - ready for practical AI tools that make a real difference. You'll quickly master techniques that save time while improving quality.",
    advanced: "You'll dive deep into sophisticated AI capabilities and build comprehensive systems that other organizations will want to study and replicate."
  };
  return storylines[techComfort as keyof typeof storylines] || "We'll match the pace to your comfort level.";
};

const getAIExperienceStoryline = (aiExperience: string): string => {
  const storylines = {
    none: "Perfect starting point! You'll learn AI from the ground up with practical applications that immediately improve your work. No prior knowledge needed.",
    little: "You've dipped your toes in the water - now we'll show you how to swim. You'll turn those early experiments into systematic wins that transform your daily workflow.",
    lots: "You understand AI's potential - now you'll apply that knowledge specifically to nonprofit work with advanced techniques that maximize mission impact."
  };
  return storylines[aiExperience as keyof typeof storylines] || "We'll build on what you already know.";
};

const getLearningStyleStoryline = (learningStyle: string): string => {
  const storylines = {
    visual: "You'll get clear diagrams, flowcharts, and visual guides that make complex AI concepts immediately understandable and actionable.",
    'hands-on': "You'll practice with interactive scenarios right away. Every concept comes with exercises you can try immediately to see tangible results.",
    conceptual: "You'll understand the 'why' behind every technique with detailed explanations and frameworks that help you adapt approaches to your specific needs.",
    story: "You'll learn through realistic scenarios and examples that mirror challenges nonprofits face, seeing how AI can transform similar work situations."
  };
  return storylines[learningStyle as keyof typeof storylines] || "We'll match our teaching style to how you learn best.";
};

const getPartialRoleImpact = (role: string): string => {
  const impacts = {
    fundraising: "Turn program updates into compelling donor stories",
    programs: "Extract powerful case studies from participant data", 
    operations: "Transform meeting notes into clear action plans",
    marketing: "Create social content that actually drives engagement",
    it: "Write technical docs that staff can actually understand",
    leadership: "Communicate strategic vision that inspires action"
  };
  return impacts[role as keyof typeof impacts] || "Apply AI effectively to your specific work challenges";
};

export const PersonalizationFlow: React.FC<PersonalizationFlowProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    role: '',
    techComfort: '',
    aiExperience: '',
    learningStyle: ''
  });
  
  const { user } = useAuth();
  const { savePersonalizationData, loading } = usePersonalizationData();
  const navigate = useNavigate();

  const steps = [
    {
      title: "What's your primary role?",
      subtitle: "Help us tailor your learning experience",
      lyraExpression: 'helping' as const,
      options: [
        { id: 'fundraising', label: 'Fundraising & Development', description: 'Grant writing, donor relations, campaigns' },
        { id: 'programs', label: 'Programs & Services', description: 'Direct service delivery, program management' },
        { id: 'operations', label: 'Operations & Admin', description: 'HR, finance, general administration' },
        { id: 'marketing', label: 'Marketing & Communications', description: 'Outreach, social media, content creation' },
        { id: 'it', label: 'IT & Technology', description: 'Tech support, digital infrastructure' },
        { id: 'other', label: 'Leadership & Other', description: 'Executive, board, volunteer roles' }
      ],
      key: 'role'
    },
    {
      title: "How comfortable are you with new technology?",
      subtitle: "Be honest - there's no wrong answer!",
      lyraExpression: 'thinking' as const,
      options: [
        { id: 'beginner', label: 'I avoid new tools when possible', description: 'Beginner-friendly pace' },
        { id: 'intermediate', label: 'I dabble and experiment sometimes', description: 'Balanced approach' },
        { id: 'advanced', label: 'I enjoy trying new tech weekly', description: 'Fast-paced learning' }
      ],
      key: 'techComfort'
    },
    {
      title: "Any prior experience with AI tools?",
      subtitle: "This helps us set the right starting point",
      lyraExpression: 'thinking' as const,
      options: [
        { id: 'none', label: 'None at all', description: 'We\'ll start from the very beginning' },
        { id: 'little', label: 'A little experimentation', description: 'Quick refresher then dive deeper' },
        { id: 'lots', label: 'I use AI tools regularly', description: 'Focus on advanced applications' }
      ],
      key: 'aiExperience'
    },
    {
      title: "How do you learn best?",
      subtitle: "Let's match your natural learning style",
      lyraExpression: 'helping' as const,
      options: [
        { id: 'visual', label: 'Visual learner', description: 'Diagrams, charts, and infographics' },
        { id: 'hands-on', label: 'Hands-on practice', description: 'Try things immediately' },
        { id: 'conceptual', label: 'Conceptual understanding', description: 'Deep dives into how things work' },
        { id: 'story', label: 'Story-based examples', description: 'Real scenarios and case studies' }
      ],
      key: 'learningStyle'
    }
  ];

  const handleAnswer = (value: string) => {
    const newAnswers = { ...answers, [steps[currentStep].key]: value };
    setAnswers(newAnswers);
    // No longer auto-advance - user must click Continue button
  };

  const handleBackToHome = () => {
    onComplete(); // Close the personalization flow to return to home page
  };

  const handleComplete = async () => {
    // Convert answers to match database field names
    const personalizationData = {
      role: answers.role,
      tech_comfort: answers.techComfort,
      ai_experience: answers.aiExperience,
      learning_style: answers.learningStyle
    };

    if (user) {
      // User is logged in, save data and go to dashboard
      await savePersonalizationData(personalizationData, user.id);
      navigate('/dashboard');
    } else {
      // User not logged in, save data temporarily and redirect to auth
      localStorage.setItem('pendingPersonalization', JSON.stringify(personalizationData));
      navigate('/auth');
    }
  };

  const currentStepData = steps[currentStep];
  const isComplete = currentStep === steps.length;
  const roleMessaging = answers.role ? getRoleMessaging(answers.role) : null;
  
  // Get enhanced dynamic messaging when all answers are available (memoized for performance)
  const dynamicMessaging = useMemo(() => {
    if (!(answers.role && answers.aiExperience && answers.techComfort)) return null;
    
    return getDynamicRoleMessaging({
      role: answers.role,
      aiExperience: answers.aiExperience as 'none' | 'little' | 'lots',
      techComfort: answers.techComfort as 'beginner' | 'intermediate' | 'advanced',
      learningStyle: answers.learningStyle as 'visual' | 'hands-on' | 'conceptual' | 'story'
    });
  }, [answers.role, answers.aiExperience, answers.techComfort, answers.learningStyle]);

  if (isComplete) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-6xl w-full">
          {/* Back to Home button */}
          <div className="flex justify-start mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToHome}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 p-2"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 items-start">
            {/* Left Column: Main Content */}
            <div className="space-y-4">
              {/* Welcome Card with Lyra */}
              <Card className="border-0 shadow-lg bg-blue-50">
                <CardContent className="p-6 text-center lg:text-left">
                  <div className="flex flex-col lg:flex-row items-center gap-4">
                    <LyraAvatar 
                      expression="celebrating"
                      size="lg"
                      className="flex-shrink-0"
                    />
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold mb-2 text-purple-600 text-wrap-safe">
                        Perfect! We Know How to Help You 🎯
                      </h2>
                      <p className="text-gray-700 text-wrap-safe">
                        Now that we've gotten to know you better, here's how we're going to have an impact on your skills and daily work.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Personalized Impact Message */}
              {(dynamicMessaging || roleMessaging) && (
                <Card className="border-0 shadow-lg bg-purple-50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-xl mb-4 text-gray-800 text-wrap-safe">
                      Your Personalized AI Training
                    </h3>
                    <div className="space-y-4">
                      <p className="text-gray-700 text-wrap-safe leading-relaxed">
                        {dynamicMessaging?.enhancedWelcomeMessage || roleMessaging?.welcomeMessage}
                      </p>
                      
                      <div className="bg-white/60 rounded-lg p-4">
                        <h4 className="font-medium text-gray-800 mb-2">What You'll Master:</h4>
                        <div className="text-sm text-gray-700 space-y-1">
                          {(dynamicMessaging?.examples || roleMessaging?.examples)?.split(', ').map((skill, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="text-purple-500 mt-1">•</span>
                              <span className="capitalize">{skill}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-blue-100 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 mb-1">Your Impact Goal</h4>
                        <p className="text-purple-700 font-medium">
                          {dynamicMessaging?.successMetric || roleMessaging?.successMetric}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>

            {/* Right Column: Summary & Actions */}
            <div className="flex flex-col space-y-4 h-full">
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm flex-shrink-0">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-lg mb-3">Your Assessment Results</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium text-sm">Role:</span>
                      <Badge variant="secondary" className="capitalize text-xs">{answers.role}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium text-sm">Tech Comfort:</span>
                      <Badge variant="secondary" className="capitalize text-xs">{answers.techComfort}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium text-sm">AI Experience:</span>
                      <Badge variant="secondary" className="capitalize text-xs">{answers.aiExperience}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2.5 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium text-sm">Learning Style:</span>
                      <Badge variant="secondary" className="capitalize text-xs">{answers.learningStyle}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-blue-50 flex-shrink-0">
                <CardContent className="p-5 text-center">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    🚀 Ready to Transform Your Work?
                  </h3>
                  <p className="text-blue-700 text-sm mb-4">
                    Create your account to save your assessment and start your personalized AI training.
                  </p>
                  
                  <div className="space-y-3">
                    <Button
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base font-semibold"
                      onClick={handleComplete}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : (user ? 'Continue to Dashboard' : 'Create Account')}
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-gray-600"
                      onClick={() => setCurrentStep(0)}
                    >
                      Retake Assessment
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Time Investment Preview */}
              <Card className="border-0 shadow-lg bg-green-50 flex-grow">
                <CardContent className="p-5 text-center h-full flex flex-col justify-center">
                  <h3 className="font-semibold text-green-800 mb-2">Time Investment</h3>
                  <div className="text-2xl font-bold text-green-600 mb-1">&lt;4 hours</div>
                  <p className="text-green-700 text-sm mb-2">Total training time</p>
                  <p className="text-xs text-green-600">
                    Broken into 7 focused chapters you can complete at your own pace
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-6xl w-full">
        {/* Back to Home button */}
        <div className="flex justify-start mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToHome}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 p-2"
          >
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Question */}
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-4">
                <LyraAvatar expression={currentStepData.lyraExpression} />
              </div>

              {/* Question counter with progress dots */}
              <div className="flex justify-center items-center gap-3 mb-4">
                <span className="text-sm font-medium text-gray-600">
                  Question {currentStep + 1} of {steps.length}
                </span>
                <div className="flex">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-3 h-3 rounded-full mx-1 transition-all duration-300 ${
                        index <= currentStep
                          ? 'bg-blue-600'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <CardTitle className="text-2xl font-bold mb-2 text-wrap-safe">
                {currentStepData.title}
              </CardTitle>
              <CardDescription className="text-base text-gray-600 text-wrap-safe mb-2">
                {currentStepData.subtitle}
              </CardDescription>
              {/* Clear instruction that buttons are the answer method */}
              <p className="text-sm text-purple-600 font-medium">
                Select one option below
              </p>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {currentStepData.options.map((option) => {
                const isRoleStep = currentStepData.key === 'role';
                const isSelected = answers[currentStepData.key as keyof typeof answers] === option.id;
                
                return (
                  <Button
                    key={option.id}
                    variant={isSelected ? "default" : "outline"}
                    className={`w-full p-4 h-auto text-left justify-start transition-all duration-200 ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-500'
                        : 'hover:bg-blue-50 hover:border-blue-300'
                    }`}
                    onClick={() => handleAnswer(option.id)}
                  >
                    <div className="flex items-start gap-4 w-full">
                      {isRoleStep && (
                        <div className={`w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm border ${
                          isSelected ? 'bg-white border-white' : 'bg-white border-gray-100'
                        }`}>
                          <img 
                            src={getUserRoleIconUrl(option.id as any)} 
                            alt={`${option.label} role`}
                            className="w-16 h-16 object-contain"
                            loading="eager"
                            onError={(e) => {
                              console.warn(`Failed to load icon for ${option.id}`);
                              // Hide broken image
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-base mb-1 text-wrap-safe">{option.label}</div>
                        {option.description && (
                          <div className={`text-sm text-wrap-safe ${isSelected ? 'text-purple-100' : 'text-gray-600'}`}>
                            {option.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </Button>
                );
              })}
              
              {currentStep > 0 && (
                <div className="pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Go Back
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right Column: Live Preview */}
          <Card className="border-0 shadow-xl bg-gray-50">
            <CardHeader>
              <CardTitle>Your Impact Preview</CardTitle>
              <CardDescription>
                See how your choices shape your personalized training
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Current Selection Preview */}
              {answers[currentStepData.key as keyof typeof answers] && (
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">Current Selection:</h4>
                  <div className="flex items-center gap-2 mb-3">
                    {currentStepData.key === 'role' && (
                      <div className="w-8 h-8 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden flex items-center justify-center">
                        <img 
                          src={getUserRoleIconUrl(answers[currentStepData.key as keyof typeof answers] as any)} 
                          alt="Selected role"
                          className="w-6 h-6 object-contain"
                        />
                      </div>
                    )}
                    <Badge variant="secondary" className="capitalize">
                      {answers[currentStepData.key as keyof typeof answers]}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {currentStepData.options.find(opt => opt.id === answers[currentStepData.key as keyof typeof answers])?.description}
                    </p>
                    {currentStepData.key === 'role' && answers.role && (
                      <div className="bg-purple-50 p-3 rounded text-sm">
                        <p className="text-purple-700">
                          {getRoleStoryline(answers.role)}
                        </p>
                      </div>
                    )}
                    {currentStepData.key === 'techComfort' && answers.techComfort && (
                      <div className="bg-blue-50 p-3 rounded text-sm">
                        <p className="text-blue-700">
                          {getTechComfortStoryline(answers.techComfort)}
                        </p>
                      </div>
                    )}
                    {currentStepData.key === 'aiExperience' && answers.aiExperience && (
                      <div className="bg-green-50 p-3 rounded text-sm">
                        <p className="text-green-700">
                          {getAIExperienceStoryline(answers.aiExperience)}
                        </p>
                      </div>
                    )}
                    {currentStepData.key === 'learningStyle' && answers.learningStyle && (
                      <div className="bg-orange-50 p-3 rounded text-sm">
                        <p className="text-orange-700">
                          {getLearningStyleStoryline(answers.learningStyle)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Live Personalization Preview */}
              {dynamicMessaging && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">Your Training Preview:</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {dynamicMessaging.enhancedWelcomeMessage}
                  </p>
                  <div className="mt-3 p-3 bg-white/60 rounded">
                    <p className="text-xs text-purple-600 font-medium">
                      Goal: {dynamicMessaging.successMetric}
                    </p>
                  </div>
                </div>
              )}

              {/* Partial Preview for Incomplete Selections */}
              {!dynamicMessaging && answers.role && (
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-700 mb-2">Your Impact Preview:</h4>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      As someone in <strong>{answers.role}</strong>, you'll gain skills that directly impact your daily work:
                    </p>
                    <div className="bg-purple-50 p-3 rounded">
                      <p className="text-sm text-purple-700">
                        • Write better content faster with AI assistance<br/>
                        • Spot quality issues before they become problems<br/>
                        • Build templates that save hours each week<br/>
                        • {getPartialRoleImpact(answers.role)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Continue Button with Integrated Next Question */}
              {answers[currentStepData.key as keyof typeof answers] && (
                <div className="pt-4">
                  <Button
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white p-4 h-auto"
                    onClick={() => {
                      if (currentStep < steps.length - 1) {
                        setCurrentStep(currentStep + 1);
                      } else {
                        // Complete assessment - advance to results screen
                        setCurrentStep(steps.length);
                      }
                    }}
                  >
                    <div className="flex flex-col items-center gap-1 w-full">
                      {currentStep === steps.length - 1 ? (
                        <span className="font-semibold">Complete Assessment</span>
                      ) : (
                        <>
                          <span className="text-sm opacity-90">Next Question:</span>
                          <span className="font-semibold">{steps[currentStep + 1]?.title}</span>
                        </>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 ml-2 flex-shrink-0" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
